import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Optional } from 'chaika-frontend-types/frontend-cms';

import { translate } from '@chaika/i18n/helper';
import { PopupModifiers } from '@components/Popup';
import { patientMobileAppQueryOptions } from '@patient/queries/patientMobileAppQueries';
import { store } from '@store';
import { notificationService } from '@store/services/notificationService';

import { PATIENT_APP_LINK } from '../../constants/patientAppLink';

const LINK_SENT_STATE_DURATION_MS = 5000;

interface Params {
    patientId: string;
}

interface Data {
    hasApp: Optional<boolean>;
    isLinkSent: boolean;
    sendAppLink(): Promise<void>;
}

export function usePatientAppLink({ patientId }: Params): Data {
    const { app, ehr } = store;

    const { data: mobileAppStatus } = useQuery(patientMobileAppQueryOptions.status(patientId));

    const [isLinkSent, setIsLinkSent] = useState(false);

    useEffect(() => {
        if (!isLinkSent) {
            return;
        }

        const timer = setTimeout(() => setIsLinkSent(false), LINK_SENT_STATE_DURATION_MS);

        return () => clearTimeout(timer);
    }, [isLinkSent]);

    const sendAppLink = async (): Promise<void> => {
        const phone = ehr.phone;

        if (!phone) {
            app.showPopup({ modifiers: [PopupModifiers.Redesign], description: translate('noPhoneNumber') });

            return;
        }

        await app.doOrShowError({
            action: () =>
                notificationService.sendAppDownloadLink({
                    ehrId: patientId,
                    phone,
                    link: PATIENT_APP_LINK,
                }),
            errorMessage: translate('error'),
        });

        setIsLinkSent(true);
    };

    return { hasApp: mobileAppStatus?.registered, isLinkSent, sendAppLink };
}
